import * as React from "react";
import { 
    Modal, 
    Stack, 
    Text, 
    Box, 
    useMantineColorScheme, 
    Group, 
    ActionIcon, 
    Tooltip, 
    CopyButton, 
    SegmentedControl,
    Title,
    ScrollArea,
    Button
} from "@mantine/core";
import { Copy, Check, FileJson, FileText, Type } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

interface ReportRowDetailsModalProps {
    opened: boolean;
    onClose: () => void;
    selectedRow: any;
}

type ViewMode = 'raw' | 'markdown' | 'json';

function RowDetailItem({ label, value }: { label: string; value: any }) {
    const { colorScheme } = useMantineColorScheme();
    const [viewMode, setViewMode] = React.useState<ViewMode>('raw');
    
    const isObject = typeof value === 'object' && value !== null;
    const isString = typeof value === 'string';
    
    const canBeJson = React.useMemo(() => {
        if (isObject) return true;
        if (isString && (value.trim().startsWith('{') || value.trim().startsWith('['))) {
            try {
                JSON.parse(value);
                return true;
            } catch (e) {
                return false;
            }
        }
        return false;
    }, [value, isObject, isString]);
    
    // Auto-detect JSON and markdown
    React.useEffect(() => {
        if (canBeJson) {
            setViewMode('json');
        } else if (isString && (value.includes('#') || value.includes('*') || value.includes('`'))) {
            setViewMode('markdown');
        }
    }, [canBeJson, isString, value]);

    const renderContent = () => {
        const bgStyle = {
            backgroundColor: colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)',
            borderRadius: 'var(--mantine-radius-md)',
            border: '1px solid var(--modal-border)',
        };

        if (viewMode === 'json') {
            try {
                const jsonValue = isObject ? value : (isString ? JSON.parse(value) : value);
                return (
                    <Box
                        p="xs"
                        style={{
                            ...bgStyle,
                            maxHeight: '400px',
                            overflow: 'auto',
                        }}
                    >
                        <pre style={{ margin: 0, fontSize: '12px', fontFamily: 'var(--mantine-font-family-monospace)' }}>
                            {JSON.stringify(jsonValue, null, 2)}
                        </pre>
                    </Box>
                );
            } catch (e) {
                return (
                    <Box p="xs" c="red" bg="rgba(255,0,0,0.05)" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
                        <Text size="xs">Error parsing JSON</Text>
                    </Box>
                );
            }
        }
        
        if (viewMode === 'markdown') {
            return (
                <Box
                    p="xs"
                    className="prose dark:prose-invert max-w-none"
                    style={{
                        ...bgStyle,
                        minHeight: '60px',
                        maxWidth: '100%',
                        overflowX: 'hidden'
                    }}
                >
                    <ReactMarkdown 
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        components={{
                            p: ({children}) => (
                                <Text 
                                    size="sm" 
                                    mb="xs" 
                                    style={{ 
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal'
                                    }}
                                >
                                    {children}
                                </Text>
                            ),
                            code: ({children}) => (
                                <Box 
                                    component="code" 
                                    p="4px" 
                                    style={{ 
                                        background: colorScheme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)', 
                                        borderRadius: '4px',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        fontSize: '0.85em'
                                    }}
                                >
                                    {children}
                                </Box>
                            ),
                            pre: ({children}) => (
                                <Box 
                                    component="pre" 
                                    style={{ 
                                        overflowX: 'auto', 
                                        maxWidth: '100%', 
                                        padding: '8px',
                                        backgroundColor: colorScheme === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.03)',
                                        borderRadius: '4px',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {children}
                                </Box>
                            )
                        }}
                    >
                        {String(value ?? '')}
                    </ReactMarkdown>
                </Box>
            );
        }

        return (
            <Box
                p="xs"
                style={{
                    ...bgStyle,
                    minHeight: '40px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    fontSize: '14px'
                }}
            >
                {String(value ?? '')}
            </Box>
        );
    };

    const copyValue = isObject ? JSON.stringify(value, null, 2) : String(value ?? '');

    return (
        <Stack gap={8} style={{ minWidth: 0 }}>
            <Group justify="space-between" align="flex-end" wrap="wrap">
                <Text 
                    fw={600} 
                    size="xs" 
                    c="dimmed" 
                    tt="uppercase" 
                    lts="0.5px"
                    style={{ 
                        flex: 1, 
                        minWidth: '120px', 
                        wordBreak: 'break-word' 
                    }}
                >
                    {label}
                </Text>
                <Group gap="xs" wrap="nowrap">
                    <SegmentedControl
                        size="xs"
                        value={viewMode}
                        onChange={(val) => setViewMode(val as ViewMode)}
                        style={{ flexShrink: 0 }}
                        data={[
                            { label: <Tooltip label="Raw Text"><Type size={14} /></Tooltip>, value: 'raw' },
                            { label: <Tooltip label="Markdown"><FileText size={14} /></Tooltip>, value: 'markdown' },
                            ...(canBeJson ? [{ label: <Tooltip label="JSON"><FileJson size={14} /></Tooltip>, value: 'json' }] : []),
                        ]}
                    />
                    <CopyButton value={copyValue} timeout={2000}>
                        {({ copied, copy }) => (
                            <Tooltip label={copied ? 'Copied' : 'Copy Value'}>
                                <ActionIcon 
                                    variant="light" 
                                    color={copied ? 'teal' : 'gray'} 
                                    size="sm" 
                                    onClick={copy}
                                    style={{ flexShrink: 0 }}
                                >
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </CopyButton>
                </Group>
            </Group>
            {renderContent()}
        </Stack>
    );
}

export function ReportRowDetailsModal({
    opened,
    onClose,
    selectedRow,
}: ReportRowDetailsModalProps) {
    const { colorScheme } = useMantineColorScheme();
    
    return (
        <Modal 
            opened={opened} 
            onClose={onClose} 
            centered
            title={
                <Group gap="xs">
                    <FileText size={20} />
                    <Title order={4}>Row Details</Title>
                </Group>
            } 
            size="xl"
            overlayProps={{
                backgroundOpacity: 0.1,
                blur: 2,
            }}
        >
            <Stack gap="md">
                <ScrollArea.Autosize mah="70vh" mx="-md" px="md">
                    <Box
                        p={{ base: 'md', sm: 'lg' }}
                        style={{
                            border: '1px solid var(--modal-border)',
                            borderRadius: 'var(--mantine-radius-md)',
                            background: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                            backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined,
                            width: '100%',
                            overflow: 'hidden'
                        }}
                    >
                        <Stack gap="xl">
                            {selectedRow && Object.entries(selectedRow).map(([key, value]) => (
                                <Box key={key} style={{ minWidth: 0 }}>
                                    <RowDetailItem label={key} value={value} />
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                </ScrollArea.Autosize>
                
                <Group justify="flex-end" pt="md">
                    <Button variant="light" color="gray" onClick={onClose}>
                        Close
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}

